/**
 * Email send service via Gobodo EmailApi.sendEmail().
 * Uses the official @gobodo/gobodo-api-promise SDK (POST /notifications/email/send).
 * Sender must be logged in; uses the authenticated Gobodo client.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../gobodo/gobodoClient';
import { shouldUseGobodo } from '../utils/gobodoUtils';
import { retryWithBackoff } from '../utils/gobodoRetry';

export interface SendEmailParams {
  to: string[];
  toUserIds?: number[];
  cc?: string[];
  ccUserIds?: number[];
  bcc?: string[];
  bccUserIds?: number[];
  subject: string;
  message: string;
  senderName?: string;
  senderEmail?: string;
}

export interface SendEmailResponse {
  sent: boolean;
  messageId?: string;
}

/**
 * Send email via Gobodo EmailApi. Requires REACT_APP_USE_GOBODO_API=true and valid auth.
 * Supports recipients by email address and/or Gobodo user ID.
 */
export async function sendEmail(params: SendEmailParams): Promise<SendEmailResponse> {
  if (!shouldUseGobodo()) {
    throw new Error('Email sending requires Gobodo API. Set REACT_APP_USE_GOBODO_API=true.');
  }

  const client = getGobodoClient();
  const emailApi = new GobodoApi.EmailApi(client);

  const input = new GobodoApi.SendEmailInput(params.subject, params.message);
  if (params.to?.length) input.setToEmails(params.to);
  if (params.toUserIds?.length) input.setToUserIds(params.toUserIds);
  if (params.cc?.length) input.setCcEmails(params.cc);
  if (params.ccUserIds?.length) input.setCcUserIds(params.ccUserIds);
  if (params.bcc?.length) input.setBccEmails(params.bcc);
  if (params.bccUserIds?.length) input.setBccUserIds(params.bccUserIds);
  if (params.senderName) input.setSenderName(params.senderName);
  if (params.senderEmail) input.setSenderEmail(params.senderEmail);

  const result = await retryWithBackoff(
    () => emailApi.sendEmail(input),
    { maxRetries: 1, initialDelayMs: 1000 }
  );

  return {
    sent: (result as { sent?: boolean })?.sent ?? true,
    messageId: (result as { messageId?: string })?.messageId,
  };
}
